import { useCallback, useState } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarProvider,
} from '@monksflow/monks-ui';
import type { Workspace } from '@/entities/workspace';
import type { User } from '@/shared/types/user.types';
import type { UserMenuItem } from '@/shared/types/user-menu.types';
import type { WorkspaceMenuHeaderAction } from '@/shared/types/workspace-menu.types';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useSidebarNavigation } from '@/widgets/app-shell/model/use-sidebar-navigation';
import { useThemeMode } from '@/shared/hooks/useThemeMode';
import { useWorkspaceSelector } from '@/shared/hooks/useWorkspaceSelector';
import {
  SidebarWorkspaceTrigger,
  SidebarWorkspacePopover,
  SidebarUserTrigger,
  SidebarUserPopover,
} from './components';
import { ICON_RAIL_PROVIDER_STYLE, IconRail, RailRow } from '../IconRail';

const SUPPORT_EMAIL = 'support@monksflow.ai';

export interface SidebarRailProps {
  user: User;
  workspaces: Workspace[];
  selectedWorkspaceId: string;
  organizationName?: string;
  organizationLogoUrl?: string | null;
  workspaceMenuHeaderAction?: WorkspaceMenuHeaderAction;
  onWorkspaceChange?: (workspace: Workspace) => void;
  onSignOutClick: () => void;
  platformAppUrl: string;
  userMenuItems?: UserMenuItem[];
  isChatBusy?: boolean;
  showAdminSettings: boolean;
}

/**
 * Sidebar rail — self-contained vertical navigation rail built on
 * the `@monksflow/monks-ui` Sidebar primitive.
 *
 * The primitive provides:
 *  - Stagger entrance animation on the menu items (`SidebarMenu` wraps
 *    children in a motion `<ul>` driven by `LayoutGroup`).
 *  - A spring-animated `layoutId` active indicator that slides between
 *    items — in `orientation="vertical"` mode it is pinned to the
 *    icon-holder area at the top of each item, so the slide animates
 *    between any two items in the menu, not just adjacent ones.
 *
 * Layout: each nav button stacks icon ABOVE label (both visible). The
 * sidebar is in `collapsible="none"` mode — there is no
 * expand/collapse logic, the rail always shows the same content at
 * 75px wide. The workspace popover and user popover remain custom
 * components mounted inside `SidebarHeader`/`SidebarFooter`.
 *
 * Layout (top → bottom):
 * 1. Header: workspace logo trigger + popover
 * 2. Content: nav items from useSidebarNavigation
 * 3. Footer: bottom nav items + user avatar trigger + popover
 */
export function SidebarRail({
  user,
  workspaces,
  selectedWorkspaceId,
  organizationName,
  organizationLogoUrl,
  workspaceMenuHeaderAction,
  onWorkspaceChange,
  onSignOutClick,
  platformAppUrl,
  userMenuItems = [],
  isChatBusy = false,
  showAdminSettings,
}: SidebarRailProps) {
  const { navigationList, bottomNavigationList } = useSidebarNavigation();
  // Gated by the `dark_mode_toggle` Statsig flag — when disabled, `enabled`
  // is false and the toggle is hidden in the user popover.
  const { isDark, toggle: toggleTheme, enabled: darkModeEnabled } = useThemeMode();
  const languageSwitcherEnabled = useBooleanFlagValue('language_switcher', false);

  const workspaceSelector = useWorkspaceSelector({
    workspaces,
    selectedWorkspaceId,
    organizationName,
    organizationLogoUrl,
    onWorkspaceChange,
    headerAction: workspaceMenuHeaderAction,
    disabled: isChatBusy,
  });

  // ── User popover state ──────────────────────────────────────────────
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopySupport = useCallback(() => {
    void navigator.clipboard.writeText(SUPPORT_EMAIL);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  }, []);

  // ── Map legacy UserMenuItem to SidebarUserMenuItem ──────────────────
  const sidebarUserMenuItems = userMenuItems.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    onClick: () => item.onClick({} as React.MouseEvent<HTMLLIElement>),
  }));

  return (
    <IconRail
      header={
        <RailRow>
          <SidebarWorkspacePopover
            open={workspaceSelector.open}
            onOpenChange={workspaceSelector.handleOpenChange}
            searchQuery={workspaceSelector.searchQuery}
            onSearchChange={workspaceSelector.setSearchQuery}
            workspaces={workspaceSelector.filteredWorkspaces}
            selectedWorkspaceId={selectedWorkspaceId}
            onSelect={workspaceSelector.handleSelect}
            organizationName={workspaceSelector.organizationName}
            organizationLogoUrl={workspaceSelector.organizationLogoUrl}
            organizationInitials={workspaceSelector.organizationInitials}
            hasHeaderAction={workspaceSelector.hasHeaderAction}
            headerActionTooltip={workspaceSelector.headerActionTooltip}
            onHeaderAction={workspaceSelector.handleHeaderAction}
          >
            <SidebarWorkspaceTrigger
              logoUrl={workspaceSelector.currentWorkspace?.logoUrl}
              workspaceName={workspaceSelector.currentWorkspace?.name}
              disabled={isChatBusy}
            />
          </SidebarWorkspacePopover>
        </RailRow>
      }
      items={navigationList}
      bottomItems={bottomNavigationList}
      footer={
        <RailRow>
          <SidebarUserPopover
            open={userPopoverOpen}
            onOpenChange={setUserPopoverOpen}
            userName={user.name}
            userEmail={user.email}
            customMenuItems={sidebarUserMenuItems}
            showAdminSettings={showAdminSettings}
            platformAppUrl={platformAppUrl}
            emailCopied={emailCopied}
            onCopySupport={handleCopySupport}
            showLanguageSwitcher={languageSwitcherEnabled}
            isDarkMode={isDark}
            onToggleTheme={darkModeEnabled ? toggleTheme : undefined}
            onSignOut={onSignOutClick}
          >
            <SidebarUserTrigger name={user.name} />
          </SidebarUserPopover>
        </RailRow>
      }
    />
  );
}

/**
 * Loading skeleton for the sidebar — shown while auth is loading.
 *
 * Mirrors the live `SidebarRail` layout: `collapsible="none"` rail at
 * 75px wide, with a logo skeleton at top, 3 nav skeletons in a centered
 * group, and 1 user skeleton at the bottom. Uses the primitive's
 * `SidebarMenuSkeleton` (built on motion's `Skeleton`) for shimmer.
 */
export function SidebarRailSkeleton() {
  return (
    <SidebarProvider defaultOpen style={ICON_RAIL_PROVIDER_STYLE}>
      <Sidebar
        collapsible="none"
        data-testid="loading-sidebar"
        className="border-r border-border-light"
      >
        <SidebarHeader className="items-center justify-center p-xs">
          <SidebarMenu className="items-center">
            <SidebarMenuItem>
              <SidebarMenuSkeleton />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent className="items-center py-3xl">
          <SidebarGroup className="p-0">
            <SidebarGroupContent>
              <SidebarMenu className="gap-md">
                <SidebarMenuItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuSkeleton />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="items-center p-xs">
          <SidebarMenu className="items-center">
            <SidebarMenuItem>
              <SidebarMenuSkeleton />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </SidebarProvider>
  );
}
